import { db } from './db';
import { getSupabase } from './supabase';
import type { SyncItem, SyncKind } from './types';

/**
 * Ordered outbox for writes that must reach the backend. Items are flushed
 * oldest-first with exponential backoff; a failure stops the pass so ordering
 * is preserved (a session result must land before the progress that follows it).
 *
 * Handlers are registered per kind. Phase 0 ships the queue mechanics with no
 * handlers; later phases register real ones. An unknown kind is dropped with a
 * warning rather than blocking the queue forever.
 */

export type SyncHandler = (payload: unknown) => Promise<void>;

/**
 * Thrown by a handler for a payload that will never succeed, such as one that
 * fails validation. Retrying it is pointless, and because the queue stops at the
 * first failure to preserve ordering, retrying would block everything behind it
 * for the whole backoff schedule. These are dropped on the spot instead.
 */
export class PermanentSyncError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PermanentSyncError';
  }
}

const handlers = new Map<SyncKind, SyncHandler>();

export function registerSyncHandler(kind: SyncKind, handler: SyncHandler): void {
  handlers.set(kind, handler);
}

/**
 * Queueing also asks for a flush, so a finished lesson reaches the backend while
 * the learner is still looking at the completion screen rather than waiting for
 * the next app start. It is debounced, because one session enqueues three items
 * in a row and they should go in a single pass.
 *
 * Only a running app flushes on its own: `startSyncWorker` turns this on, so
 * tests drive `flush()` themselves and stay deterministic.
 */
const ENQUEUE_FLUSH_MS = 300;
let autoFlush = false;

export async function enqueue(kind: SyncKind, payload: unknown): Promise<void> {
  await db.syncQueue.add({
    kind,
    payload,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
  });
  if (autoFlush) scheduleRetry(ENQUEUE_FLUSH_MS);
}

export async function pendingCount(): Promise<number> {
  return db.syncQueue.count();
}

const MAX_ATTEMPTS = 8;
/** Backoff in ms by attempt index: 1s, 2s, 4s … capped at ~5 min. */
function backoffMs(attempts: number): number {
  return Math.min(1000 * 2 ** attempts, 5 * 60_000);
}

let flushing = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;

export interface FlushResult {
  sent: number;
  failed: number;
  skipped: boolean;
}

export async function flush(): Promise<FlushResult> {
  if (flushing) return { sent: 0, failed: 0, skipped: true };
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { sent: 0, failed: 0, skipped: true };
  }
  if ((await getSupabase()) === null) return { sent: 0, failed: 0, skipped: true };

  flushing = true;
  let sent = 0;
  let failed = 0;

  try {
    // Oldest first; stop at the first failure to keep ordering guarantees.
    for (;;) {
      const item: SyncItem | undefined = await db.syncQueue.orderBy('id').first();
      if (!item) break;

      const handler = handlers.get(item.kind);
      if (!handler) {
        console.warn(`[sync] no handler for "${item.kind}" — dropping item ${item.id ?? '?'}`);
        if (item.id !== undefined) await db.syncQueue.delete(item.id);
        continue;
      }

      try {
        await handler(item.payload);
        if (item.id !== undefined) await db.syncQueue.delete(item.id);
        sent += 1;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);

        if (err instanceof PermanentSyncError) {
          console.error(`[sync] dropping unusable "${item.kind}": ${message}`);
          if (item.id !== undefined) await db.syncQueue.delete(item.id);
          failed += 1;
          continue;
        }

        failed += 1;
        const attempts = item.attempts + 1;
        if (item.id !== undefined) {
          if (attempts >= MAX_ATTEMPTS) {
            console.error(`[sync] dropping "${item.kind}" after ${attempts} attempts: ${message}`);
            await db.syncQueue.delete(item.id);
          } else {
            await db.syncQueue.update(item.id, { attempts, lastError: message });
            scheduleRetry(backoffMs(attempts));
          }
        }
        break;
      }
    }
  } finally {
    flushing = false;
  }

  return { sent, failed, skipped: false };
}

function scheduleRetry(ms: number): void {
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flush();
  }, ms);
}

/** Flush when the tab regains connectivity or visibility. */
export function startSyncWorker(): () => void {
  autoFlush = true;
  const onOnline = (): void => void flush();
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void flush();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  void flush();
  return () => {
    autoFlush = false;
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    if (retryTimer !== null) clearTimeout(retryTimer);
  };
}

/**
 * Test seam: clears registered handlers and cancels any scheduled retry so a
 * backoff timer from one test cannot fire during the next.
 */
export function __resetForTests(): void {
  handlers.clear();
  autoFlush = false;
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  flushing = false;
}
